import { NEUTRAL, SERIES } from "../../palette";
import type { HistSeries, MetricSeries, PodSnapshot } from "../../types";

/**
 * The fixed order of pods on the tab: the live pool first (in pool order), then pods that only appear
 * in the window's metrics (removed since), sorted. Pod i always wears SERIES[i] in every card, chart and
 * table of the tab; past the fourth slot a pod is grey (the palette is never cycled).
 */
export function podOrder(live: PodSnapshot[] | undefined, metrics: MetricSeries | undefined): string[] {
  const order: string[] = [];
  const seen = new Set<string>();
  const add = (endpoint: string) => {
    if (!seen.has(endpoint)) {
      seen.add(endpoint);
      order.push(endpoint);
    }
  };
  live?.forEach((pod) => add(pod.endpoint));
  if (metrics) {
    const extra = new Set<string>();
    for (const group of [
      metrics.counters,
      metrics.hists,
      metrics.maxes,
      metrics.totals?.counters ?? {},
      metrics.totals?.hists ?? {},
    ]) {
      for (const key of Object.keys(group)) {
        const at = key.indexOf("@");
        if (key.startsWith("pod.") && at > 0) extra.add(key.slice(at + 1));
      }
    }
    [...extra].sort().forEach(add);
  }
  return order;
}

export const podColor = (index: number): string => SERIES[index] ?? NEUTRAL.strong;

/** pods past the categorical palette start hidden in the per-pod charts */
export const podBeyondPalette = (index: number) => index >= SERIES.length;

/** a counter series of the window (zeros when the metric never fired) */
export const counterSeries = (metrics: MetricSeries, name: string): number[] =>
  metrics.counters[name] ?? metrics.t.map(() => 0);

export const histField = (metrics: MetricSeries, name: string, field: keyof HistSeries): (number | null)[] =>
  metrics.hists[name]?.[field] ?? [];

export const hasHist = (metrics: MetricSeries, name: string) =>
  (metrics.hists[name]?.n ?? []).some((n) => typeof n === "number" && n > 0);

/** a per-minute max gauge of the window (gaps where nothing was sampled) */
export const maxField = (metrics: MetricSeries, name: string): (number | null)[] => metrics.maxes[name] ?? [];

export const hasMax = (metrics: MetricSeries, name: string) =>
  (metrics.maxes[name] ?? []).some((v) => typeof v === "number");
